import { inject, Injectable } from "@angular/core";
import {
  CreateDataWarehouseExportRequest,
  DataWarehouseApi,
  DataWarehouseResource,
  DataWarehouseResponse,
  DataWarehouseShare,
  GCloudLocation,
  Organization,
  UpdateDataWarehouseExportRequest,
} from "@front/m19-api-client";
import { EMPTY, Observable, switchMap, take, tap } from "rxjs";
import { OrganizationWritableCache } from "@front/m19-utils";
import { AccountSelectionService, OrganizationService } from "@front/m19-services";

export type DataWarehouseShares = DataWarehouseShare[];

@Injectable({ providedIn: "root" })
export class DataWarehouseService {
  private readonly dataWarehouseApi = inject(DataWarehouseApi);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly organizationService = inject(OrganizationService);

  static readonly SHARES_LIMIT = 5;
  static readonly SHARE_NAME_MAX_LENGTH = 45;

  protected readonly dataSharesCache = new OrganizationWritableCache((organizationId: number) =>
    this.getDataWarehouseShares(organizationId),
  );

  public readonly dataShares$ = this.accountSelectionService.selectedOrganizationAccountGroups$.pipe(
    switchMap((organization) => this.dataSharesCache.get(organization.id)),
  );

  private getDataWarehouseShares(organizationId: number): Observable<DataWarehouseShares> {
    return this.dataWarehouseApi.listDataWarehouseExports({ organizationId });
  }

  public createDataWarehouseShare(
    shareName: string,
    gcPrincipals: Array<string>,
    gcLocation: GCloudLocation,
    resources: DataWarehouseResource[],
  ): Observable<DataWarehouseResponse> {
    return this.accountSelectionService.selectedOrganizationAccountGroups$.pipe(
      take(1),
      switchMap((org) => {
        const req: CreateDataWarehouseExportRequest = {
          organizationId: org.id,
          dataWarehouseShare: {
            organizationId: org.id,
            shareName: shareName,
            gcLocation: gcLocation,
            gcPrincipals: gcPrincipals,
            resources: resources,
            deleted: false,
          },
        };
        return this.dataWarehouseApi.createDataWarehouseExport(req);
      }),
      tap((response) => {
        this.dataSharesCache.update(response.entity.organizationId, (shares) => [...shares, response.entity]);
      }),
    );
  }

  public updateDataWarehouseShare(
    shareId: number,
    gcPrincipals: Array<string>,
    resources: DataWarehouseResource[],
    deleted: boolean,
  ): Observable<DataWarehouseResponse> {
    return this.accountSelectionService.selectedOrganizationAccountGroups$.pipe(
      take(1),
      switchMap((org) => {
        const req: UpdateDataWarehouseExportRequest = {
          organizationId: org.id,
          dataWarehouseShare: {
            shareId: shareId,
            organizationId: org.id,
            gcPrincipals: gcPrincipals,
            resources: resources,
            shareName: "",
            gcLocation: GCloudLocation.NORTHAMERICA_NORTHEAST2,
            deleted: deleted,
          },
        };
        return this.dataWarehouseApi.updateDataWarehouseExport(req).pipe(
          tap((_) => {
            this.dataSharesCache.update(org.id, (shares) => {
              return shares.map((share) =>
                share.shareId === shareId ? { ...share, resources, gcPrincipals, deleted } : share,
              );
            });
          }),
        );
      }),
    );
  }

  public activateDataWarehouseFreeTest() {
    return this.accountSelectionService.selectedOrganizationAccountGroups$.pipe(
      take(1),
      switchMap((org) => {
        return this.dataWarehouseApi.activateDataWarehouseTest({ organizationId: org.id }).pipe(
          switchMap((response) => {
            const entity = response.entity as Organization | undefined;
            if (!entity) return EMPTY;
            return this.organizationService.updateOrganization({ ...entity, ...org });
          }),
        );
      }),
    );
  }
}
